import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Layout, PageHeader, Button, Input, Spin, message, Table, Select } from 'antd'
import Highlighter from 'react-highlight-words'
import HeaderNavigation from '../../components/HeaderNavigation'

import styles from './style.module.css'
import { getEstimateFieldByProject, updateEstimateField } from '../../services/estimate-field'
import AddEstimateModal from '../../components/estimateItem/EstimateItemModal'
import UpdateEstimateModal from '../../components/estimateItem/UpdateEstimateModal'
import { useSelector } from 'react-redux'
import { userSelector } from '../../store/session'
import useCategory from '../../components/hooks/useCategory'
import CategorySelectorModal from '../../components/CategorySelectorModal'

const { Content } = Layout
const { Search } = Input

const Estimate = () => {
  const [searchParams] = useSearchParams()
  let navigate = useNavigate()
  const { user } = useSelector(userSelector)
  const estimateId = useMemo(() => searchParams.get('estimateId'), [searchParams])
  const project = useMemo(() => searchParams.get('project'), [searchParams])
  const [categories] = useCategory(project)
  const [isLoading, setIsLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [selectedEstimateItem, setSelectedEstimateItem] = useState(null)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedKeys, setSelectedKeys] = useState([])
  const [showCategorySelectorModal, setShowCategorySelectorModal] = useState(false)

  const isProjectManager = useMemo(() => user?.role?.type === 'project_manager', [user])
  const isEstimator = useMemo(() => user?.role?.type === 'estimator', [user])

  const pagination = useMemo(
    () => ({
      current: page,
      pageSize,
      total: count,
      onChange: setPage,
      showSizeChanger: true,
      onShowSizeChange: (current, size) => {
        setPageSize(size)
      },
      showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`,
      pageSizeOptions: [5, 10, 20, 50, 100, 200, 300],
    }),
    [count, page, pageSize]
  )

  const getData = async () => {
    const response = await getEstimateFieldByProject(project, searchValue, selectedCategories, { page, pageSize })
    if (response && response.status === 200 && response.data) {
      setData(response.data?.data?.attributes?.results)
      setCount(response.data?.data?.attributes?.pagination?.total)
    }
  }

  const updateData = () => {
    setIsLoading(true)
    return getData()
      .catch(() => message.error('Ошибка загрузки контента'))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => updateData(), [project, searchValue, selectedCategories, page, pageSize])

  const onSearchChange = (e) => {
    setSearchValue(e.target.value)
    setPage(1)
  }

  const onCategoriesChange = (values) => {
    setSelectedCategories(values)
  }

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      render: (name) => <Highlighter searchWords={searchValue.split(' ')} autoEscape textToHighlight={name} />,
    },
    {
      title: 'Цена',
      dataIndex: 'price',
    },
    {
      title: 'Кол-во',
      dataIndex: 'amount',
    },
    {
      title: 'Ед. Изм.',
      dataIndex: 'unit',
    },
    {
      title: 'Категория',
      dataIndex: 'category',
    },
    {
      title: 'Идентификатор',
      dataIndex: 'identifier',
    },
    {
      title: 'Действия',
      dataIndex: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          disabled={!isProjectManager && !isEstimator}
          onClick={() => {
            setSelectedEstimateItem(record)
            setShowAddModal(true)
          }}
        >
          Редактировать
        </Button>
      ),
    },
  ]

  const onAddNewHandler = () => {
    setSelectedEstimateItem(null)
    setShowAddModal(true)
  }

  const onModalCloseHandler = () => {
    setShowAddModal(false)
    getData()
      .catch(() => message.error('Ошибка загрузки контента'))
      .finally(() => setIsLoading(false))
  }

  const onUpdateModalCloseHandler = () => {
    setShowUpdateModal(false)
    getData()
      .catch(() => message.error('Ошибка загрузки контента'))
      .finally(() => setIsLoading(false))
  }

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys) => setSelectedKeys(keys),
    preserveSelectedRowKeys: true,
  }

  const onCategoryUpdateClose = () => {
    setShowCategorySelectorModal(false)
  }

  const onCategoryUpdate = (category) => {
    setIsLoading(true)
    const promises = selectedKeys.map((key) =>
      updateEstimateField(key, {
        category: category,
      })
    )
    Promise.all(promises)
      .then(() => {
        setSelectedKeys([])
        setSelectedCategories([category])
      })
      .catch(() => message.error('Ошибка обновления или загрузки контента'))
      .finally(() => {
        setIsLoading(false)
        setShowCategorySelectorModal(false)
      })
  }

  return (
    <Layout>
      <HeaderNavigation activeKey="2" />
      <Content className={styles.content}>
        <PageHeader
          ghost={false}
          title="Редактирование сметы"
          extra={[
            <Button key="1" onClick={() => navigate(-1)}>
              Закрыть
            </Button>,
            <Button disabled={!isProjectManager && !isEstimator} key="2" onClick={() => setShowUpdateModal(true)}>
              Загрузить смету с обновлениями
            </Button>,
            <Button
              disabled={selectedKeys.length === 0}
              key="3"
              onClick={() => setShowCategorySelectorModal(true)}
              type="primary"
            >
              Обновить категорию
            </Button>,
            <Button disabled={!isProjectManager && !isEstimator} key="4" type="primary" onClick={onAddNewHandler}>
              Добавить новую позицию
            </Button>,
          ]}
        />
        <Spin spinning={isLoading}>
          <div className={styles.search}>
            <Search
              style={{ flex: 1 }}
              allowClear
              placeholder="Введите название позиции"
              onChange={onSearchChange}
              loading={isLoading}
            />
            <Select
              value={selectedCategories}
              style={{ flex: 1, marginLeft: 15 }}
              mode="multiple"
              allowClear
              options={categories.map((category) => ({
                value: category,
                label: category,
              }))}
              placeholder="Выберите категорию"
              dropdownMatchSelectWidth={false}
              onChange={onCategoriesChange}
            />
          </div>
          <Table
            rowKey="id"
            style={{ paddingTop: 20, cursor: 'pointer' }}
            size="small"
            columns={columns}
            dataSource={data}
            pagination={pagination}
            rowSelection={rowSelection}
          />
        </Spin>
      </Content>
      <AddEstimateModal
        showModal={showAddModal}
        estimateId={estimateId}
        estimateItem={selectedEstimateItem}
        onChancel={onModalCloseHandler}
        onSuccess={() => {
          updateData()
          selectedEstimateItem(null)
        }}
      />
      <CategorySelectorModal
        categories={categories}
        showModal={showCategorySelectorModal}
        onChancel={onCategoryUpdateClose}
        onFinish={onCategoryUpdate}
      />
      <UpdateEstimateModal estimateId={estimateId} showModal={showUpdateModal} onCancel={onUpdateModalCloseHandler} />
    </Layout>
  )
}

export default Estimate
